import { FC, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { KTSVG } from "../../../../../../_metronic/helpers";
import { getAuth } from "../../../../../modules/auth";
import Swal from "sweetalert2";

interface LocationState {
  moduleId?: number;
}

interface MetricaInput {
  nome_metrica: string;
  valor: string;
}

export const AvaliacaoForm: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const auth = getAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const state = location.state as LocationState;
  const moduleId = state?.moduleId || 1;

  const [loading, setLoading] = useState(false);
  const [atletas, setAtletas] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    _id_atleta_ficha: "",
    tipo: "Física",
    avaliador_nome: "",
    data_avaliacao: new Date().toISOString().split("T")[0],
    observacoes: "",
    resultado_geral: "",
  });

  const [metricas, setMetricas] = useState<MetricaInput[]>([
    { nome_metrica: "Peso (kg)", valor: "" },
    { nome_metrica: "Altura (cm)", valor: "" },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (!auth?.api_token) return;

      const headers = { Authorization: `Bearer ${auth.api_token}` };

      try {
        const resAtletas = await axios.get(
          `${apiUrl}/technical-committee/athletes`,
          {
            params: { moduleId },
            headers,
          },
        );
        setAtletas(resAtletas.data);

        if (id) {
          setLoading(true);
          const resAvaliacao = await axios.get(
            `${apiUrl}/technical-committee/evaluations/${id}`,
            { headers },
          );
          const data = resAvaliacao.data;

          setFormData({
            _id_atleta_ficha: data._id_atleta_ficha,
            tipo: data.tipo,
            avaliador_nome: data.avaliador_nome,
            data_avaliacao: data.data_avaliacao
              ? data.data_avaliacao.split("T")[0]
              : "",
            observacoes: data.observacoes || "",
            resultado_geral: data.nota_geral || data.resultado_geral || "",
          });

          if (data.metricas && data.metricas.length > 0) {
            setMetricas(data.metricas);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl, auth?.api_token, moduleId, id]);

  const handleMetricaChange = (
    index: number,
    field: keyof MetricaInput,
    value: string,
  ) => {
    const newMetricas = [...metricas];
    newMetricas[index][field] = value;
    setMetricas(newMetricas);
  };

  const addMetrica = () => {
    setMetricas([...metricas, { nome_metrica: "", valor: "" }]);
  };

  const removeMetrica = (index: number) => {
    const newMetricas = metricas.filter((_, i) => i !== index);
    setMetricas(newMetricas);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData._id_atleta_ficha) {
      Swal.fire("Atenção", "Selecione um atleta.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        _id_companhia: 1,
        metricas: metricas.filter((m) => m.nome_metrica && m.valor),
      };

      const headers = { Authorization: `Bearer ${auth?.api_token}` };

      if (id) {
        await axios.put(
          `${apiUrl}/technical-committee/evaluations/${id}`,
          payload,
          { headers },
        );
        Swal.fire({
          icon: "success",
          title: "Avaliação atualizada!",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        await axios.post(
          `${apiUrl}/technical-committee/athletes/evaluation`,
          payload,
          { headers },
        );
        Swal.fire({
          icon: "success",
          title: "Avaliação criada!",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      navigate(-1);
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Falha ao salvar avaliação.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          {id
            ? "Editar Avaliação"
            : `Nova Avaliação - ${moduleId === 1 ? "Competição" : "Escolinha"}`}
        </h3>
        <div>
          <button
            type="button"
            className="btn btn-light btn-sm me-3 ms-3 mt-5"
            onClick={() => navigate("/technical-committee")}
          >
            Voltar
          </button>
        </div>
      </div>
      <div className="card-body w-100">
        <form onSubmit={handleSubmit}>
          <div className="row mb-5">
            <div className="col-md-6">
              <label className="form-label required">Atleta</label>
              <select
                className="form-select form-select-sm"
                required
                value={formData._id_atleta_ficha}
                onChange={(e) =>
                  setFormData({ ...formData, _id_atleta_ficha: e.target.value })
                }
              >
                <option value="">Selecione o Atleta...</option>
                {atletas.map((atleta: any) => (
                  <option
                    key={atleta._id_atleta_ficha}
                    value={atleta._id_atleta_ficha}
                  >
                    {atleta.nome} {atleta.sobrenome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label required">Data</label>
              <input
                type="date"
                className="form-control form-control-sm"
                required
                value={formData.data_avaliacao}
                onChange={(e) =>
                  setFormData({ ...formData, data_avaliacao: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Nota Geral (0-10)</label>
              <input
                type="number"
                step="0.1"
                className="form-control form-control-sm"
                value={formData.resultado_geral}
                onChange={(e) =>
                  setFormData({ ...formData, resultado_geral: e.target.value })
                }
              />
            </div>
          </div>

          <div className="row mb-8">
            <div className="col-md-6">
              <label className="form-label required">Tipo de Avaliação</label>
              <select
                className="form-select form-select-sm"
                value={formData.tipo}
                onChange={(e) =>
                  setFormData({ ...formData, tipo: e.target.value })
                }
              >
                <option value="Física">Física</option>
                <option value="Técnica">Técnica</option>
                <option value="Tática">Tática</option>
                <option value="Psicológica">Psicológica</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label required">Avaliador</label>
              <input
                type="text"
                className="form-control form-control-sm"
                required
                placeholder="Nome do avaliador"
                value={formData.avaliador_nome}
                onChange={(e) =>
                  setFormData({ ...formData, avaliador_nome: e.target.value })
                }
              />
            </div>
          </div>

          <h4 className="mb-4 text-gray-700">Métricas e Resultados</h4>
          <div className="bg-light p-5 rounded mb-8">
            {metricas.map((metrica, index) => (
              <div key={index} className="row mb-4 align-items-end">
                <div className="col-md-5">
                  <label className="form-label fs-7">
                    Métrica (Ex: Peso, Pace, Salto)
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={metrica.nome_metrica}
                    onChange={(e) =>
                      handleMetricaChange(index, "nome_metrica", e.target.value)
                    }
                    placeholder="Nome da métrica"
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label fs-7">Valor / Resultado</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={metrica.valor}
                    onChange={(e) =>
                      handleMetricaChange(index, "valor", e.target.value)
                    }
                    placeholder="Valor medido"
                  />
                </div>
                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-icon btn-sm btn-light-danger"
                    onClick={() => removeMetrica(index)}
                  >
                    <KTSVG
                      path="/media/icons/duotune/general/gen027.svg"
                      className="svg-icon-3"
                    />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-light-primary mt-2"
              onClick={addMetrica}
            >
              <KTSVG
                path="/media/icons/duotune/arrows/arr075.svg"
                className="svg-icon-3"
              />
              Adicionar Métrica
            </button>
          </div>

          <div className="mb-5">
            <label className="form-label">Observações / Parecer Técnico</label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              value={formData.observacoes}
              onChange={(e) =>
                setFormData({ ...formData, observacoes: e.target.value })
              }
            ></textarea>
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn btn-light me-3 btn-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {loading
                ? "Salvando..."
                : id
                ? "Atualizar Avaliação"
                : "Salvar Avaliação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
